import axios, { type AxiosInstance } from "axios";

import { notifyRequestEnd, notifyRequestStart } from "./progress.js";

/**
 * The one HTTP client. Ported from `ecom_code/client/src/lib/axiosInstance.js`, with
 * three deliberate changes — each of which would be a live defect if copied as-is.
 *
 * 1. `X-Tenant-Id` NO LONGER COMES FROM localStorage.
 *
 *    Legacy read `selectedShop.tenantId`, a uuid LEGACY generated. v3's store ids are
 *    different values entirely, and `stores.legacy_tenant_id` was dropped in 6.1. The
 *    only source now is `/auth/me`, which returns the stores this session may actually
 *    open — so the header can never name a store the caller has no membership for.
 *
 * 2. THE INTERCEPTOR MUST NOT REDIRECT ON 402.
 *
 *    Legacy redirects to "/" on 401 AND 403. 402 is `SUBSCRIPTION_REQUIRED` — a billing
 *    refusal, introduced in 6.2 precisely so a paywall does not look like a logout.
 *    Catching it here would bounce a merchant to the login page when their trial ends,
 *    instead of showing them a banner offering to take their money. That single line is
 *    why the code is 402 and not 403 in the first place.
 *
 * 3. CROSS-ORIGIN WITH CREDENTIALS.
 *
 *    Legacy served the bundle from the same Express process as the API, so this was
 *    same-origin and none of it mattered. `app.ecomally.in` calling `api.ecomally.in` is
 *    cross-origin — `withCredentials` is what sends the session cookie, and the CORS
 *    allowlist plus the host-only cookie from 6.1 are what let it through.
 */

/**
 * WHERE THE API URL COMES FROM, and why it is not baked in.
 *
 * Vite substitutes `import.meta.env` at BUILD time, so reading it here would tie one
 * bundle to one API host — and doc 06 §5 says one image is promoted by digest across
 * dev → uat → prod with behaviour coming only from typed config. So the app asks its own
 * server for `/config.json` at boot, and the server answers from ITS environment.
 *
 * Validated at the boundary (.ai/01): a config file that does not parse must fail loudly
 * at startup, not become `undefined` inside an axios baseURL and produce requests to a
 * URL nobody can explain.
 */
export interface RuntimeConfig {
  readonly apiUrl: string;
  readonly appEnv: string;
  /** Feature flags enabled for this environment (from `/config.json`). Drives per-env UI
   *  gating — e.g. the Billing nav item — without an `if (appEnv === …)` in the code. */
  readonly features: readonly string[];
}

export async function loadRuntimeConfig(fetchImpl: typeof fetch = fetch): Promise<RuntimeConfig> {
  const response = await fetchImpl("/config.json", { cache: "no-store" });
  if (!response.ok) throw new Error(`Could not load /config.json (${String(response.status)})`);

  const body: unknown = await response.json();
  if (
    typeof body !== "object" ||
    body === null ||
    typeof (body as { apiUrl?: unknown }).apiUrl !== "string" ||
    (body as { apiUrl: string }).apiUrl === ""
  ) {
    throw new Error("/config.json is missing a usable apiUrl");
  }
  const parsed = body as { apiUrl: string; appEnv?: unknown; features?: unknown };
  const features = Array.isArray(parsed.features) ? parsed.features.filter((entry): entry is string => typeof entry === "string") : [];
  return { apiUrl: parsed.apiUrl, appEnv: typeof parsed.appEnv === "string" ? parsed.appEnv : "unknown", features };
}

export class BillingRequiredError extends Error {
  public override readonly name = "BillingRequiredError";
  constructor(message: string) {
    super(message);
  }
}

export interface ApiClientOptions {
  /** From `loadRuntimeConfig`. Passed in rather than read here, so this stays testable. */
  readonly baseURL: string;
  /** Supplies the current store id. Null means "no store selected yet". */
  readonly currentTenantId: () => string | null;
  /** Called on 401 — the session is gone and the app must return to sign-in. */
  readonly onUnauthenticated: () => void;
  /** Called on 402 — the plan does not cover this. NOT a logout. */
  readonly onBillingRequired: (message: string) => void;
}

/** Never refreshed on: refreshing a failed login is a loop, not a recovery. */
const NO_REFRESH = ["/auth/login", "/auth/refresh", "/auth/logout", "/auth/password/set"];

export function createApiClient(options: ApiClientOptions): AxiosInstance {
  /**
   * ONE refresh in flight at a time.
   *
   * A dashboard fires several report calls at once, and when the access token expires they
   * all 401 together. Without this each would POST its own `/auth/refresh` — and because
   * refresh tokens ROTATE, the first would invalidate the token the others are still
   * holding, the server would see a reused token, and replay detection would revoke the
   * whole session. The bug would present as "it logs me out when I open two reports".
   */
  let refreshing: Promise<void> | null = null;

  /**
   * The refresh call goes through a SEPARATE instance with no interceptors.
   *
   * Sent through the main client, a failed refresh is itself a 401 and re-enters this
   * handler — so the session was reported as ended twice, and the app re-rendered the
   * login screen on top of itself. Keeping it outside means exactly one place decides
   * that the session is over.
   */
  const refresher = axios.create({ baseURL: options.baseURL, withCredentials: true });

  const client = axios.create({
    baseURL: options.baseURL,
    headers: { "Content-Type": "application/json" },
    // Sends the session cookie cross-origin. Without it every request is anonymous and
    // the failure looks like "the API rejects me" rather than "no cookie was sent".
    withCredentials: true,
  });

  client.interceptors.request.use((config) => {
    // Every request through this client feeds the global top progress bar. The matching
    // end fires in BOTH response handlers below, so each start is balanced exactly once.
    notifyRequestStart();
    const tenantId = options.currentTenantId();
    if (tenantId !== null) config.headers.set("X-Tenant-Id", tenantId);
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      notifyRequestEnd();
      return response;
    },
    (error: unknown) => {
      // Balance the start from the request interceptor. A failed request settles too, and
      // leaving it counted would pin the top bar on screen forever after any error. The
      // 401 refresh path below re-sends through `client.request`, which starts a FRESH
      // request (and its own end), so ending here does not lose the retry's own bar.
      notifyRequestEnd();
      /**
       * `axios.isAxiosError`, not `instanceof AxiosError`. The prototype check fails
       * whenever the error crosses a realm or a duplicated axios copy in the bundle —
       * and when it fails, this interceptor silently stops handling 401s and 402s while
       * every request still "works". The flag check is the documented form and does not
       * have that failure mode.
       */
      const status = axios.isAxiosError(error) ? error.response?.status : undefined;

      if (status === 401) {
        /**
         * TRY TO REFRESH BEFORE GIVING UP. The access token lives an hour
         * (AUTH_ACCESS_TTL_SECONDS); the refresh token lives thirty days. The API has had
         * `POST /auth/refresh` with rotation and replay detection since 6.1 — the
         * dashboard simply never called it, so every session ended after exactly an hour
         * and the merchant read that as being logged out at random.
         */
        const request = axios.isAxiosError(error) ? error.config : undefined;
        const url = request?.url ?? "";
        const alreadyRetried = (request as { _retried?: boolean } | undefined)?._retried === true;

        if (request !== undefined && !alreadyRetried && !NO_REFRESH.some((path) => url.startsWith(path))) {
          // Marked BEFORE the retry, so a second 401 on the same request signs out
          // instead of refreshing forever.
          (request as { _retried?: boolean })._retried = true;

          refreshing ??= refresher
            .post("/auth/refresh")
            .then(() => undefined)
            .finally(() => {
              refreshing = null;
            });

          return refreshing.then(
            async () => await client.request(request),
            () => {
              // The refresh itself failed — the session is genuinely over.
              options.onUnauthenticated();
              return Promise.reject(error instanceof Error ? error : new Error(String(error)));
            },
          );
        }

        options.onUnauthenticated();
      } else if (status === 402) {
        /**
         * Deliberately NOT a redirect. The merchant stays where they are and sees what
         * their plan does not include — "your trial has ended, choose a plan" is a
         * conversion moment, and bouncing them to a login screen wastes it.
         */
        const data = axios.isAxiosError(error) ? (error.response?.data as unknown) : undefined;
        const message =
          typeof data === "object" && data !== null && typeof (data as { message?: unknown }).message === "string"
            ? (data as { message: string }).message
            : "Your plan does not include this.";
        options.onBillingRequired(message);
        return Promise.reject(new BillingRequiredError(message));
      }
      /**
       * 403 is NOT redirected either, unlike legacy. Here it means "this store is not
       * yours" — which, after `/auth/me` has told the app which stores are, can only
       * happen if access was revoked mid-session. Showing that is more useful than
       * silently logging someone out.
       */

      return Promise.reject(error instanceof Error ? error : new Error(String(error)));
    },
  );

  return client;
}

/**
 * The message the API sent, or a fallback — narrowed, never stringified.
 *
 * Both auth screens originally did `String(response.data.message ?? fallback)`. The
 * linter rejected it and was right: `message` is `unknown`, and `String({...})` renders
 * **"[object Object]"** — which is what the merchant would have read on a failed login.
 * The type has to be checked, not coerced.
 */
export function apiErrorMessage(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) return fallback;

  const data: unknown = error.response?.data;
  if (typeof data !== "object" || data === null) return fallback;

  const message: unknown = (data as { message?: unknown }).message;
  return typeof message === "string" && message !== "" ? message : fallback;
}
